import { useEffect, useRef } from 'react';
import {
  AmbientLight,
  Box3,
  BoxGeometry,
  Color,
  DirectionalLight,
  Group,
  Mesh,
  MeshPhongMaterial,
  PerspectiveCamera,
  Raycaster,
  Euler,
  Matrix3,
  Matrix4,
  Quaternion,
  Scene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { cleanRenderer, cleanScene, textureLoader } from 'utils/three';
import { boardGeometry } from './boardGeometry';
import styles from './BookStack3D.module.css';
import {
  SPINE_FONTS,
  backTexture,
  luminanceOf,
  boardTexture,
  boardBumpTexture,
  clothBumpTexture,
  coverPalette,
  glitterTexture,
  pagesTexture,
  plainCover,
  spineFontReady,
  spineTexture,
} from './bookTextures';
import { books } from './data/books';
import {
  depthRatioFor,
  phiAt,
  placeAt,
  WHEEL_PER_BOOK,
} from './stackGeometry';

/* The WebGL stack.

   Same books, same curve, same scroll as the CSS version -- see
   stackGeometry.js, which both import. Only the drawing differs, which is the
   point: anything else and the comparison measures two things at once.

   The material model is lifted from press.stripe.com, which exposes its
   per-book settings in a `js-book-materials` block: a Phong surface with a
   diffuse map, a bump map for the cloth, and a specular map, at
   shininess ~3 and reflectivity ~0.6. Notably there is no mesh to load --
   their `thickness` is a parameter, so the box is generated. Same here. */

/* World units per CSS pixel. The curve is authored in px; the scene works in
   units around 1, which keeps the near/far planes and light distances sane. */
const UNIT = 0.01;
/* The book's long edge, in the same px the curve uses. */
const BOOK_LENGTH = 920;
/* Degrees. Long, deliberately -- see the camera below. */
const FOV = 14;
/* A case-bound book, in the same px the curve uses. The SQUARE is how far the
   boards overhang the text block on the head, tail and fore-edge -- about 3mm
   on a real 230mm book -- and it is most of what separates a bound book from a
   printed slab. */
const SQUARE = 16;
const BOARD_T = 9;
const SPINE_T = 9;
/* Corner radius of the boards, in px. Slight -- a board is rounded off, not
   a lozenge. */
const BOARD_R = 6;
/* Book thickness, in px, covered by one tile of the page texture. */
const PAGE_TILE = 52;

/* The strip of board between the spine edge and where the cover art starts, as
   a fraction of the board's width. A real case-bound board is wider than its
   front artwork by exactly this -- the hinge the cover flexes on -- and the
   French groove lives inside it. Setting the whole board to the art's aspect,
   as the first pass did, left the groove no room and it cut into the art. */
const HINGE = 0.06;
/* How far the focused book turns toward the pointer, in radians. Bounded well
   under the resting yaw below: swing it as far as the pose itself and the
   pointer can cancel the three-quarter entirely, leaving the book square-on
   and flat. Following the cursor closely is the ease, not the amplitude. */
const TRACK_YAW = 0.3;
const TRACK_PITCH = 0.18;
/* Fraction of the remaining distance covered per frame -- an exponential ease,
   so it trails the cursor rather than snapping to it. */
const TRACK_EASE = 0.14;
/* Air to leave either side of the run. The books are authored at a fixed
   length in px, and .stackView gives up 320px of its width to padding that
   clears the sidebar, so on anything much under 1240px of viewport the stack
   is wider than the surface it is drawn on and its corners get clipped. */
const FIT_MARGIN = 40;
/* Where the book sits across the viewport in the reader, as a fraction. Must
   match --heroCol in Bookshelf.module.css, which reserves 2x this for the
   column the book is drawn in. */
/* The book is centred in the space actually left for it: from the rail's
   gutter on the left to where the copy column starts on the right. A fixed
   fraction of the viewport put it 60px from the rail and 146px from the text,
   which reads as leaning.

   HERO_COL and HERO_GAP match --heroCol and the band's column gap; the gutter
   matches the rail's own inset plus its rules. */
const READER_GUTTER = 72;
const HERO_COL = 0.44;
const HERO_GAP = 48;
/* The strip of page the pile is laid out in, in CSS px, matching .stackView's
   padding. Held here rather than measured because the canvas goes full bleed
   in the reader: measured, the stage would change the moment a book opened,
   and the pile's size and centre would change with it. */
const STAGE_LEFT = 192;
const STAGE_RIGHT = 128;
/* Below this the nav is a bar across the top rather than a rail down the
   side, so the pile has the whole width and the insets above do not apply.
   Matches --mediaTablet. */
const NARROW = 1040;
const NARROW_MARGIN = 20;
/* Where the top of the run sits, as a fraction of viewport height from the
   middle. Matches the CSS version's anchor. */
const TOP_ANCHOR = 0.36;
/* Fraction of a screen height to slide the image up by. Nought puts the first
   book on the middle of the screen with clear air above it, which is what lets
   you see its top board -- pinned to the top edge there is nowhere for the
   board to be drawn. */
const VIEW_SHIFT = 0;

/* How long the book takes to travel from the pile to the hero position. */
const FOCUS_MS = 760;
const easeInOut = t =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/* Nought until x passes nought, then a rate that ramps from nought to one over
   `k` and stays there.
 *
 * This is the handover curve. A hard clamp pins the book and releases it at
 * the full scroll speed in a single frame -- continuous in position,
 * discontinuous in velocity, and that step is what reads as a snap. A
 * smoothstep blend fixes the step but overshoots, briefly carrying the book
 * FASTER than the page scrolls. This is the quadratic whose derivative is
 * exactly the ramp, so the book starts from rest and never outruns the scroll.
 */
const ramp = (x, k) => {
  if (x <= 0) return 0;
  if (x >= k) return x - k / 2;
  return (x * x) / (2 * k);
};

/* How much of a screen the handover is spread over.
 *
 * This sets how far apart two books are when they cross. The ramp lags the
 * true position by k/2, so at the symmetric crossing the gap between the
 * outgoing book and the incoming one is 2*ramp(ch/2, k) -- and a longer
 * handover means a smaller gap.
 *
 * The gap has to clear the book's own on-screen height. Both scale with the
 * viewport, so the whole thing is a ratio: the book measures 0.53 of the
 * viewport, and the gap is 2 * ramp(ch/2, k), which is (1 - HANDOVER) of it.
 * At 0.45 that left 0.02 of a viewport between two books passing -- about
 * 19px, which is a collision as far as the eye is concerned. At 0.3 the gap is
 * 0.70 against a 0.53 book, so they pass with a clear margin. */
const HANDOVER = 0.3;

/* Filtering and sorting. A book moving to a new place on the belt eases there
   rather than jumping, and one leaving the shelf slides out sideways first --
   the same two gestures as the filter mock. Fractions of the remaining
   distance per frame, so a long reorder and a short one settle together. */
const RERANK_EASE = 0.16;
const LEAVE_EASE = 0.2;
/* How far a leaving book travels sideways, in px, before it is hidden. */
const LEAVE_X = 260;

export function BookStack3D({
  offsetRef,
  readerRef,
  onPick,
  onAccent,
  focusedSlug,
  shelf,
  headRef,
  className,
}) {
  const canvasRef = useRef(null);
  /* The callbacks, held by reference.
   *
   * This effect builds the whole scene -- renderer, meshes, materials and ten
   * cover textures -- so it must run exactly once. Naming the callbacks as
   * dependencies meant it re-ran whenever their identity changed, and onPick
   * is rebuilt every time the router changes, which is on every click. The
   * scene was torn down and rebuilt mid-flight, restarting every texture load,
   * and the book flew as a flat slab of accent until its cover decoded again.
   * That is the flash.
   */
  const onPickRef = useRef(onPick);
  const onAccentRef = useRef(onAccent);
  useEffect(() => {
    onPickRef.current = onPick;
    onAccentRef.current = onAccent;
  }, [onPick, onAccent]);
  const stateRef = useRef(null);
  const focusRef = useRef({ slug: null, t: 0, from: 0, since: 0 });
  const pointerRef = useRef({ x: 0, y: 0, tx: 0, ty: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.outputColorSpace = SRGBColorSpace;

    const scene = new Scene();
    /* A long lens. The reference reads nearly orthographic with just a little
       convergence; a wide one puts the lower books so far off-axis that their
       top boards swing fully into view and every book becomes a wedge. */
    const camera = new PerspectiveCamera(FOV, 1, 0.1, 200);
    camera.position.set(0, 0, 40);

    /* Soft key from the top left with a dim fill opposite, which is what gives
       the cloth bump something to catch. Ambient only lifts the shadows. */
    const key = new DirectionalLight(0xffffff, 2.1);
    key.position.set(-4, 7, 6);
    const fill = new DirectionalLight(0xffffff, 0.5);
    fill.position.set(5, -2, 3);
    const ambient = new AmbientLight(0xffffff, 0.55);
    scene.add(key, fill, ambient);

    const group = new Group();
    scene.add(group);

    /* The single biggest thing for how sharp this looks. The boards are seen at
       a glancing angle in the pile, and without anisotropic filtering a texture
       viewed that steeply samples far down the mip chain and turns to mush --
       which is what made the spine and cover lettering blurry. Nothing about
       the material fixes that; this does. */
    const maxAniso = renderer.capabilities.getMaxAnisotropy();
    const sharp = texture => {
      texture.anisotropy = maxAniso;
      texture.needsUpdate = true;
      return texture;
    };

    /* The cloth in shadow: mixed toward a neutral dark rather than scaled
       down. Scaling keeps the hue but crushes a pale cover to a grey that the
       paper beside it matches; mixing holds the hue AND guarantees separation,
       so the case still reads as one piece of cloth turning a corner rather
       than as three panels in three colours. */
    const SHADOW = new Color(0x1a1714);
    const shade = colour => colour.clone().lerp(SHADOW, 0.55);

    const cloth = sharp(clothBumpTexture());
    const glitter = sharp(glitterTexture());
    const pages = sharp(pagesTexture());

    /* Nominal cover width, used for the curve only. Every book's real width
       comes off its own cover art below, but the pile's spacing is authored
       against one figure -- as the thickness already is. */
    const w = BOOK_LENGTH / 1.5;
    const rebuilders = [];
    /* The widest board on the shelf, which is what the fit below has to clear. */
    let widest = w;
    const meshes = books.map(book => {
      const depth = BOOK_LENGTH * depthRatioFor(book.pageCount);
      /* Every book is the same LENGTH; only the width varies, which is how a
         shelf actually looks -- trade hardbacks are near enough one height and
         differ in how wide and how fat they are. The width is the cover's own
         aspect, so a book is still just a cover file. */
      let bookW = w;

      /* The manifest wins over anything sampled. A cover tells you roughly
         what colour a book is; the manifest is for when you know exactly. */
      const spec = book.case || {};
      const accentHex = spec.colour || book.cover?.accent || '#333333';
      const accent = new Color(accentHex);
      const font = SPINE_FONTS[spec.font] || undefined;
      /* 0 is a smooth printed case, 1 is full bookbinding linen. Named for
         the amount, not the material: `cloth` up there is the shared weave
         texture, and shadowing it cost a broken scene. */
      const clothAmount = spec.cloth ?? 1;

      /* The bump has to TILE. Shared across faces of different sizes, a single
         512px weave stretched to fit is invisible -- which is why the first
         pass looked like flat card. Each face gets its own clone with a repeat
         set from that face's real dimensions, so the thread pitch is constant
         across the whole book. */
      const clothFor = (uSpan, vSpan) => {
        const t = sharp(cloth.clone());
        /* One tile per ~300px of face. The weave's threads are 4px in a
           1024px tile, so this lands them a little over a pixel wide on
           screen -- fine linen. The old divisor squeezed a whole tile into
           26px, which minified the weave into flat grey. */
        t.repeat.set(uSpan / 300, vSpan / 300);
        return t;
      };
      const glitterFor = (uSpan, vSpan) => {
        const t = sharp(glitter.clone());
        t.repeat.set(uSpan / 90, vSpan / 90);
        return t;
      };

      const boards = (uSpan, vSpan) =>
        new MeshPhongMaterial({
          color: accent,
          bumpMap: clothFor(uSpan, vSpan),
          bumpScale: 8,
          specularMap: glitterFor(uSpan, vSpan),
          specular: new Color(0x3a3a3a),
          shininess: 3,
          reflectivity: 0.6,
        });
      /* One paper material for the whole block. Every cut edge shows leaves
         stacked along the THICKNESS, so all three tile the same way and none
         of them tiles along the width -- which the previous version did on the
         head and the tail, giving them a leaf pitch five times finer than the
         fore-edge's on the same book. */
      const blockT = depth - BOARD_T * 2;
      const paperMat = (() => {
        const t = sharp(pages.clone());
        t.repeat.set(1, blockT / PAGE_TILE);
        return new MeshPhongMaterial({ map: t, shininess: 2, specular: 0x222222 });
      })();
      /* The cut edge of a board: cloth turned over greyboard, and much darker
         than the face -- it is nearly edge-on to the key and it is what frames
         the text block. At a gentle darkening a pale cover gives a pale edge
         indistinguishable from the paper next to it, and the whole sandwich
         stops reading.

         Collected, because these are the only materials whose colour has to
         follow the cover -- everything else that shows the accent ends up
         carrying a map instead. A material copies the Color it is given rather
         than holding on to it, so retinting has to be done by hand. */
      const edges = [];
      const edge = () => {
        const mat = new MeshPhongMaterial({ color: shade(accent), shininess: 3 });
        edges.push(mat);
        return mat;
      };
      /* The pastedown, seen at the squares. Plain, and barely lit in there. */
      const endpaper = () =>
        new MeshPhongMaterial({ color: 0xe6dfd0, shininess: 1 });

      /* BoxGeometry face order is +x, -x, +y, -y, +z, -z: head, tail, top,
         bottom, spine side, fore-edge.

         A book is NOT one slab. The case is two boards and a spine strip, and
         the text block sits inside them, smaller on three sides -- the squares
         are the overhang, and they are most of why a hardback reads as one.
         A single box cannot express that: BoxGeometry is a closed shell, so a
         recessed block inside it would simply be hidden. Hence four parts. */
      /* The boards are rounded, so they are not boxes and their materials run
         [top, bottom, edge] -- see boardGeometry. */
      const frontMat = [boards(BOOK_LENGTH, bookW - SPINE_T), endpaper(), edge()];
      const backMat = [endpaper(), boards(BOOK_LENGTH, bookW - SPINE_T), edge()];
      const spineMat = [edge(), edge(), edge(), edge(), boards(BOOK_LENGTH, depth), edge()];

      const front = new Mesh(new BoxGeometry(1, 1, 1), frontMat);
      const back = new Mesh(new BoxGeometry(1, 1, 1), backMat);
      const spineSlab = new Mesh(new BoxGeometry(1, 1, 1), spineMat);
      const block = new Mesh(new BoxGeometry(1, 1, 1), paperMat);
      const parts = [front, back, spineSlab, block];

      /* Lay the parts out for a given cover width. Geometry is thrown away and
         rebuilt rather than scaled: scaling would take the length and the
         thickness with it, and those are meant to be fixed. */
      const shape = () => {
        for (const part of parts) part.geometry.dispose();
        /* The boards stop where the spine starts. Run to the full width they
           overlap it, and their outer edge lands coplanar with the spine's
           face -- two surfaces at the same depth, which the depth buffer
           cannot choose between, so the spine stripes when seen from the
           front. Butting them is also how a case is actually made. */
        const boardW = bookW - SPINE_T;
        front.geometry = boardGeometry(
          BOOK_LENGTH * UNIT, BOARD_T * UNIT, boardW * UNIT, BOARD_R * UNIT);
        back.geometry = boardGeometry(
          BOOK_LENGTH * UNIT, BOARD_T * UNIT, boardW * UNIT, BOARD_R * UNIT);
        spineSlab.geometry = new BoxGeometry(
          BOOK_LENGTH * UNIT, depth * UNIT, SPINE_T * UNIT);
        block.geometry = new BoxGeometry(
          (BOOK_LENGTH - SQUARE * 2) * UNIT,
          (depth - BOARD_T * 2) * UNIT,
          (boardW - SQUARE) * UNIT
        );

        front.position.set(0, ((depth - BOARD_T) / 2) * UNIT, (-SPINE_T / 2) * UNIT);
        back.position.set(0, (-(depth - BOARD_T) / 2) * UNIT, (-SPINE_T / 2) * UNIT);
        spineSlab.position.set(0, 0, ((bookW - SPINE_T) / 2) * UNIT);
        /* Butted against the inside of the spine strip, so the squares fall on
           the head, the tail and the fore-edge, which is where they belong. */
        block.position.set(0, 0, ((SQUARE - SPINE_T) / 2) * UNIT);
      };
      shape();

      const setWidth = next => {
        if (Math.abs(next - bookW) < 1) return;
        bookW = next;
        if (bookW > widest) widest = bookW;
        shape();
      };

      /* Both boards get the French groove: the channel the cover flexes in,
         just in from the spine, with the board proud either side of it. It
         happens once at a known place, so it needs a map of its own rather
         than the tiled cloth -- a repeat cannot say "here and nowhere else".

         This also takes the weave OFF the boards, which it had no business
         being on: the front board is printed art, and crosshatching it made
         the cover look like it had been photographed through a screen door.
         The cloth stays on the spine, the one bare-cloth face. */
      frontMat[0].bumpMap = sharp(boardBumpTexture({ hinge: HINGE }));
      frontMat[0].bumpScale = 7;
      backMat[1].bumpMap = sharp(boardBumpTexture({ hinge: HINGE, flip: true }));
      backMat[1].bumpScale = 7;

      /* The spine carries the title and author, and the lettering behaves like
         foil: brighter specular so it catches the light off-axis, and a little
         emissive so it still reads when the face is turned away. That pairing
         is what Stripe's foilSpecular/foilEmissive do, and it is the difference
         between stamped type and printed type. */
      let inkHex = spec.ink || '#f2ece1';
      let currentAccent = accentHex;
      const applySpine = () => {
        const spine = spineTexture(book, {
          accent: currentAccent,
          ink: inkHex,
          ratio: depth / BOOK_LENGTH,
          font,
          authorLines: spec.authorLines,
          cloth: clothAmount,
        });
        spineMat[4].map?.dispose();
        spineMat[4].map = sharp(spine.map);
        spineMat[4].specularMap?.dispose();
        spineMat[4].specularMap = sharp(spine.foilMap);
        spineMat[4].emissiveMap = spine.foilMap;
        /* Foil only where foil makes sense. Light lettering on a dark case is
           stamped in metal leaf: it catches the key off-axis and wants a lift
           so it still reads turned away. Dark lettering on a pale case is a
           blind stamp -- no leaf at all -- and giving it the same treatment
           lights the ink from within, so the type ghosts instead of reading
           solid. That is what pale covers did the moment the manifest let
           them have dark ink. */
        const leaf = luminanceOf(inkHex) > 0.4;
        /* The weave is lit as well as printed, so the relief has to follow
           the same dial -- painting the cloth away but leaving the bumps
           lights a texture that is no longer there. */
        spineMat[4].bumpScale = 12 * clothAmount;
        spineMat[4].specular = new Color(leaf ? 0xb9b2a4 : 0x2e2b26);
        spineMat[4].shininess = leaf ? 26 : 8;
        spineMat[4].emissive = new Color(leaf ? 0x2a2620 : 0x000000);
        spineMat[4].color.set(0xffffff);
        spineMat[4].needsUpdate = true;
      };
      applySpine();
      /* And again once the real face has actually arrived. A canvas font
         string resolves at draw time and falls back silently, so the first
         pass is stamped in whatever was loaded at the time. */
      rebuilders.push(applySpine);

      /* The back board is not blank. */
      backMat[1].map = sharp(backTexture(book, { accent: accentHex }));
      backMat[1].color.set(0xffffff);
      backMat[1].needsUpdate = true;

      /* The artwork, or for a book with none, a plain cover drawn from its
         title. Both arrive here as an image and are treated alike. */
      const applyCover = (image, texture) => {
        {
          const aspect = image.naturalWidth / image.naturalHeight;
          /* The ART gets the cover's true aspect; the BOARD is that plus the
             hinge, so the groove has somewhere to be that is not on top of the
             artwork. */
          if (aspect > 0.1 && aspect < 2) {
            /* The BOARD gets the art plus its hinge; the book is that plus the
               spine, which is not board. */
            setWidth((BOOK_LENGTH * aspect) / (1 - HINGE) + SPINE_T);
          }

          /* Retint everything else from the cover now that it has decoded.
             The data file's accent is only a fallback for the frames before
             this lands -- the real one comes off the artwork, so a new book
             needs a cover and nothing more. */
          const palette = coverPalette(image, accentHex);
          /* Sampling only fills the gaps the manifest left. */
          const found = spec.colour || palette.accent;
          const foundInk = spec.ink || palette.ink;
          if (found !== currentAccent || foundInk !== inkHex) {
            accent.set(found);
            inkHex = foundInk;
            currentAccent = found;
            applySpine();
            for (const mat of edges) mat.color.copy(shade(accent));

            backMat[1].map?.dispose();
            backMat[1].map = sharp(backTexture(book, { accent: found }));
            backMat[1].needsUpdate = true;

            onAccentRef.current?.(book.slug, found);
          }

          /* The board is a composite: the artwork with its hinge strip
             alongside, built in boardTexture. The strip cannot be left to the
             wrap mode -- clamp-to-edge streaks whatever happens to be in the
             cover's edge column down the whole hinge. */
          const board = boardTexture(image, { hinge: HINGE });
          sharp(board.map);
          sharp(board.bump);

          /* Its UV transform, written out rather than dialled in with
             rotation/offset/repeat -- those compose in an order that is easy to
             get wrong, and this is two lines of algebra. The face's u runs
             along the book's length and v across its width, and the image
             wants its top at u=0 and its left edge at v=0:

                s = v      t = 1 - u                                        */
          const uv = new Matrix3().set(0, 1, 0, -1, 0, 1, 0, 0, 1);
          for (const t of [board.map, board.bump]) {
            t.matrixAutoUpdate = false;
            t.matrix = uv;
          }
          texture?.dispose();

          frontMat[0].map?.dispose();
          frontMat[0].map = board.map;
          /* The board's own relief replaces the generic one now that there is
             artwork to derive it from -- printed ink with a thickness, rather
             than a blank board with a groove in it. */
          frontMat[0].bumpMap?.dispose();
          frontMat[0].bumpMap = board.bump;
          frontMat[0].bumpScale = 4;
          frontMat[0].color.set(0xffffff);
          frontMat[0].needsUpdate = true;
          /* The scene draws on demand, so an async texture that lands after
             the first frame is never shown unless it asks for another. */
          schedule();
        }
      };
      if (book.cover?.src) {
        textureLoader.load(book.cover.src, texture => {
          texture.colorSpace = SRGBColorSpace;
          applyCover(texture.image, texture);
        });
      } else {
        const drawPlain = () => applyCover(plainCover(book, { accent: accentHex, font }));
        /* After the scene has finished building: applyCover asks for a frame,
           and the frame loop is declared further down this effect. */
        queueMicrotask(drawPlain);
        /* Redrawn once the spine face has loaded; before that the canvas
           falls back to Helvetica without saying so. */
        rebuilders.push(drawPlain);
      }

      /* One book is a group of four meshes now, but everything downstream --
         the curve, the flight, the raycast -- still wants to address it as a
         single object, so the slug goes on the group and on every part. */
      const mesh = new Group();
      mesh.userData.slug = book.slug;
      mesh.userData.parts = parts;
      /* Where it sits on the belt, eased toward `target`; and how far it has
         left the shelf, eased toward `gone`. See setShelf. */
      mesh.userData.rank = mesh.userData.target = books.indexOf(book);
      mesh.userData.out = mesh.userData.gone = 0;
      for (const part of parts) {
        part.userData.slug = book.slug;
        mesh.add(part);
      }
      group.add(mesh);
      return mesh;
    });

    let live = true;
    spineFontReady().then(() => {
      if (!live) return;
      rebuilders.forEach(fn => fn());
      schedule();
    });

    const raycaster = new Raycaster();
    const pointer = new Vector2();

    /* Where a book lands on screen, in CSS pixels. The flight into the detail
       view is a FLIP and needs a rect to fly from; in the DOM version that was
       the element's own box, and here it has to be projected off the mesh. */
    /* The hero orientation, stated as a basis rather than as Euler angles.

       Euler order is the trap here: three applies the third rotation about the
       axis the first two already moved, so "tip it up, then stand it on end"
       spins the cover away from the camera instead of keeping it square to it.
       Naming where each of the book's own axes should end up removes the
       ambiguity entirely, and slerping to it gives a natural arc for free.

       The spine is the constraint. It is the +z face, and a real book wears it
       on the LEFT with the cover toward you -- so +z has to land on world -x.
       Fixing that and keeping the cover on +z forces the length axis to point
       down, which is fine: the cover art's own orientation is set separately
       by the texture rotation below.

       spine (+z) -> left, cover (+y) -> toward camera, length (+x) -> down. */
    const heroQuat = new Quaternion().setFromRotationMatrix(
      new Matrix4().makeBasis(
        new Vector3(0, -1, 0),
        new Vector3(0, 0, 1),
        new Vector3(-1, 0, 0)
      )
    );
    /* Well off square, so it reads as an object rather than an elevation.
       Turned so the SPINE comes toward you, not the fore-edge: the spine is
       the side with the title on it and is what the reference shows.

       All three axes carry some, which is what makes it read as a photographed
       object: yaw to open the spine, pitch to bring the page block at the
       tail into view, and a few degrees of roll so it is not standing to
       attention.

       The pitch is NEGATIVE. The book's length axis points down here, so its
       tail is the +x face; a positive turn about world X tips that face away
       and shows the head instead, which is the opposite of what is wanted.
       A near-square elevation with a token yaw reads as an image of a book
       rather than a book.

       Pre-multiplied, so these happen about the WORLD axes: applied the other
       way round they turn about the book's own axes after the basis has moved
       them, and yaw leaks into roll. */
    heroQuat.premultiply(
      new Quaternion().setFromEuler(new Euler(-0.26, 0.42, -0.075))
    );
    const restQuat = new Quaternion();
    const restEuler = new Euler();
    const trackQuat = new Quaternion();
    const trackEuler = new Euler();

    const box = new Box3();
    const corner = new Vector3();
    const screenRect = mesh => {
      box.setFromObject(mesh);
      const rect = canvas.getBoundingClientRect();
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < 8; i++) {
        corner.set(
          i & 1 ? box.max.x : box.min.x,
          i & 2 ? box.max.y : box.min.y,
          i & 4 ? box.max.z : box.min.z
        );
        corner.project(camera);
        const x = rect.left + ((corner.x + 1) / 2) * rect.width;
        const y = rect.top + ((1 - corner.y) / 2) * rect.height;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
      return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
    };

    /* The drawing surface, in CSS pixels. The CANVAS's own box, not its
       parent's: .stackView is padded to clear the fixed sidebar, and
       clientWidth includes padding, so the parent measured 320px wider than
       the surface actually being drawn on. The camera was built at that wider
       aspect and every horizontal dimension came out squashed to 0.79 -- which
       is what made covers read as the wrong shape, and what made the flight
       into the detail view look like it changed proportions. */
    const surface = () => {
      const parent = canvas.parentElement;
      /* Guard the first call. On mount the element can still be laid out at
         zero, and every camera number here is derived from its height -- at
         zero the camera ends up at the origin, in the same plane as the books,
         and the projection degenerates to NaN. Nothing draws and nothing ever
         corrects it, because resize only fired once. */
      return {
        cw: canvas.clientWidth || parent.clientWidth || window.innerWidth,
        ch: canvas.clientHeight || parent.clientHeight || window.innerHeight,
      };
    };

    /* What the camera and the drawing buffer were last built for. layout()
       checks these so it can never place a book using a canvas box the camera
       has not been told about yet. */
    let builtW = 0;
    let builtH = 0;

    const resize = () => {
      const { cw, ch } = surface();
      builtW = cw;
      builtH = ch;
      renderer.setSize(cw, ch, false);
      camera.aspect = cw / ch;
      /* Frame the curve to the viewport height however tall the window is, so
         the WebGL stack sits where the CSS one does. */
      camera.position.z = (ch * UNIT) / (2 * Math.tan((FOV * Math.PI) / 360));
      /* Level with the top of the run. Left at y=0 the camera sits below a
         stack that has been pushed up the screen, so every book is seen from
         underneath and you get the undersides instead of the boards. At this
         height the top book is edge-on and each one below is seen slightly
         more from above -- which is the J. */
      camera.position.y = ch * TOP_ANCHOR * UNIT;
      /* An off-centre frustum, not a tilt. The camera has to stay level with
         the top book for that book to read edge-on, but the run should sit
         high on the screen rather than centred. Tilting to achieve that would
         change every angle in the shot; shifting the frustum just moves the
         image. */
      camera.setViewOffset(cw, ch, 0, ch * VIEW_SHIFT, cw, ch);
      camera.updateProjectionMatrix();
      /* layout() owns the horizontal offset and caches it; this call has just
         thrown that away. */
      lastShiftX = null;
    };

    /* How many books are on the shelf after filtering; bounds the wheel. */
    let shelfSize = meshes.length;
    let lastShiftX = null;
    const layout = () => {
      /* Rebuild first if the surface has moved under us. Opening a book drops
         the canvas's padding, so its width and left edge change on that frame
         -- but resize() runs off a ResizeObserver, which lands a frame later.
         In that gap layout was placing books against the new canvas rect
         through a projection still built for the old one, and the book jumped
         about 120px sideways and back. */
      {
        const now = surface();
        if (now.cw !== builtW || now.ch !== builtH) resize();
      }
      const offset = offsetRef.current;
      const depth = BOOK_LENGTH * depthRatioFor(400);
      const focus = focusRef.current;
      const { cw, ch } = surface();

      /* Standing up, turned to its cover, parked on the left where the copy
         leaves room. This is the pose the picked book travels to -- the same
         mesh the whole way, never a copy of it. */
      /* Shrink the whole run to fit the width when the surface is too narrow
         for a 920px book. Scaling the group rather than moving the camera
         keeps the top of the curve anchored where it is -- pulling the camera
         back would take the anchor with it. */
      /* Sized against the NEAR face, not the centre line. A book is a slab
         several hundred pixels deep lying broadside to the camera, so its
         fore-edge sits that much closer than its middle and projects wider --
         12% wider on the widest book here. Fitting the centre line leaves the
         corners hanging over the edge, which is exactly what they did. */
      const reader = readerRef?.current;
      const flare =
        camera.position.z / (camera.position.z - (widest / 2) * UNIT);

      /* Everything below is placed in PAGE coordinates and converted through
         the canvas's live rect, rather than being placed against the canvas
         itself.

         The canvas is padded to clear the sidebar while the shelf is up and
         goes full bleed once a book opens, so its width and its centre both
         change on the frame you click. Anything anchored to the canvas moved
         with it: the pile jumped sideways and rescaled at the start of every
         flight, and again on the way back. The stage does not change, so
         nothing anchored to it does either. */
      /* Subtracting the sidebar's width on a phone left 55px of stage for a
         920px book, which is why the shelf was a postage stamp in the middle
         of the screen. */
      const narrow = window.innerWidth <= NARROW;
      const stageLeft = narrow ? NARROW_MARGIN : STAGE_LEFT;
      const stageRight =
        window.innerWidth - (narrow ? NARROW_MARGIN : STAGE_RIGHT);
      const stageWidth = stageRight - stageLeft;
      const pileCentre = (stageLeft + stageRight) / 2;
      const canvasLeft = canvas.getBoundingClientRect().left;

      const fit = Math.min(
        1,
        (stageWidth - FIT_MARGIN * 2) / (BOOK_LENGTH * flare)
      );
      group.scale.setScalar(fit);

      /* Shift the IMAGE, not the world.
      
         Offsetting the group moves things in world space, and a world-space
         shift projects smaller the further away a thing is -- so books deeper
         in the pile were compensated less than the top one, and jumped to the
         right by their own depth's worth when the canvas went full bleed on
         click. An off-centre frustum translates the whole rendered image
         uniformly at every depth, which is what this actually wants.

         Guarded because it costs a projection matrix rebuild. */
      group.position.x = 0;
      const shiftX = pileCentre - (canvasLeft + cw / 2);
      if (shiftX !== lastShiftX) {
        lastShiftX = shiftX;
        camera.setViewOffset(cw, ch, -shiftX, ch * VIEW_SHIFT, cw, ch);
        camera.updateProjectionMatrix();
      }

      /* Centred in the gutter .hero reserves on the left, so the copy column
         never runs over the book. Divided out of the group's fit: a single
         upright book is a quarter of the pile's width and never needed the
         shrink, so it should not inherit it. */
      /* One definition of where a focused book sits across the screen, used
         by the flight AND by the reader. Relative to the pile's centre,
         because that is what mesh x is measured from, and undoing the group's
         fit because a single upright book never needed the pile's shrink. */
      /* Measured when the reader has measured them, constants otherwise --
         the flight starts before the reader's first measure lands. */
      const gutter = reader?.gutter ?? READER_GUTTER;
      const copyLeft =
        reader?.copyLeft ?? window.innerWidth * HERO_COL + HERO_GAP;
      /* The hero box is only meaningful in the STACKED layout, where the
         stylesheet gives the spacer a real height and the book goes above the
         copy. Beside the copy the spacer is an empty grid item: it measures
         zero tall, it is centred in the first row rather than in the band, and
         its left edge is the band's, not the gutter's. Believed there, it
         pushed the book 190px up and pulled it left into the rail. */
      const measured = reader?.hero ?? null;
      const heroBox = measured && measured.height > 40 ? measured : null;
      const heroPageX = heroBox
        ? heroBox.left + heroBox.width / 2
        : (gutter + copyLeft) / 2;
      const heroX = ((heroPageX - pileCentre) * UNIT) / fit;
      /* Group space, and it has to undo the frustum shift. The camera renders
         an off-centre window so the pile hangs from the top of the screen; a
         hero left at nought would inherit that and sit high. */
      const heroY = (-ch * VIEW_SHIFT * UNIT) / fit;
      /* Where the flight lands. Stacked, the slot sits above the copy rather
         than mid-screen, and landing mid-screen left the reader to snap the
         book up into it on the first frame it took over. The reader opens at
         the top of the first band, so the slot's inset is its screen position. */
      const landing = heroBox ? heroBox.inset + heroBox.height / 2 - ch / 2 : 0;
      const flightY = heroY - (landing * UNIT) / fit;
      /* Sized by BOTH axes, not just height.
      
         Height alone meant a tall narrow window scaled the book up until it
         was wider than the column left for it -- it covered the copy, ran off
         the left of the screen, and the taller the window the worse it got.
         The book has to fit the space in the direction it is actually short
         of, so take whichever constraint bites first.

         Against `widest` rather than this book's own width so that every book
         on the shelf is drawn at one size; sizing each to its own cover would
         make a wide book and a narrow one different heights. */
      const heroColumn = Math.max(120, heroBox?.width ?? copyLeft - gutter);
      const heroHeight = heroBox?.height ?? ch * 0.46;
      const heroScale =
        Math.min(
          1.5,
          (Math.min(heroHeight, ch) * 0.92) / BOOK_LENGTH,
          (heroColumn * 0.86) / widest
        ) / fit;

      /* --- the reader ----------------------------------------------------
         Once a book is open the page becomes a scrolling document, one band
         per book, and the scene follows the scroll rather than a focus
         parameter. Each book's mesh behaves exactly like `position: sticky`
         against its own band: parked at the middle of the viewport for as
         long as its band covers it, and carried by the band's edges on the
         way in and out. That is what makes a book hold still while you read
         it and then hand over to the next one.

         Reading straight from a ref rather than props: this runs per frame
         off a scroll handler, and routing it through React state would
         re-render the page on every wheel tick. */
      /* Only once the flight has actually landed. Taking over while the book
         is still in the air skips the flight entirely -- the reader is live
         from the moment a book opens, not from the moment it arrives. */
      if (reader?.active && reader.bands.length && focus.t >= 0.999) {
        const half = ch / 2;
        meshes.forEach(mesh => {
          const band = reader.bands.find(b => b.slug === mesh.userData.slug);
          if (!band) {
            mesh.visible = false;
            return;
          }
          const top = band.top - reader.scrollY;
          const bottom = top + band.height;

          /* Keyed off the band's EDGES, not its middle.
          
             Against the middle the hold scales with the band, so a book with a
             long review stayed pinned far too long and was still sitting in
             the centre of the screen when the next book had already arrived --
             the two overlapped. Against the edges the handover begins exactly
             when the band stops covering the viewport, which is the same
             moment the next band appears, however long either one is.

             Below: carried down while the band is still arriving, carried up
             once it starts leaving, parked at nought in between. The two terms
             cannot both be live, because a band is never shorter than the
             screen, so the book never moves faster than the page. */
          const k = ch * HANDOVER;
          /* Parked over the hero box rather than the middle of the screen --
             the same thing on a wide layout, where the hero is centred, and
             the difference between the book sitting above the copy or on top
             of it on a narrow one. */
          const anchor = half + (heroBox?.offset ?? 0);
          /* Stacked, the book sits in its slot above the copy and scrolls with
             it like any other element. Held sticky there, it stayed pinned
             mid-screen while the copy ran underneath it, and the next book
             rode up into it. */
          const centre = heroBox
            ? top + heroBox.inset + heroBox.height / 2
            : anchor + ramp(top, k) - ramp(ch - bottom, k);
          /* Drawn only while its own band is actually on screen. The bound
             used to be a loose distance from the middle, which kept books
             alive well below the fold for no reason. A band either covers some
             of the viewport or it does not. */
          const onScreen = bottom > 0 && top < ch;
          mesh.visible = onScreen;
          if (!onScreen) return;

          /* The hero pose is yawed and rolled, so the book's projected box is
             not centred on its own origin -- placing the origin at the middle
             of the gap left it leaning about 11px toward the rail. Corrected
             from the box actually drawn last frame, which settles in one. */
          const corr = mesh.userData.xCorr || 0;
          /* Divided by the fit like heroX and heroY: the group is scaled, so an
             unscaled offset moved the book faster than the page it rides on. */
          mesh.position.set(heroX + corr, heroY - ((centre - half) * UNIT) / fit, 0);
          mesh.quaternion.copy(heroQuat);
          const track = pointerRef.current;
          if (track.x || track.y) {
            trackEuler.set(track.y * TRACK_PITCH, track.x * TRACK_YAW, 0);
            trackQuat.setFromEuler(trackEuler);
            mesh.quaternion.premultiply(trackQuat);
          }
          mesh.scale.setScalar(heroScale);
          for (const part of mesh.userData.parts) part.renderOrder = 999;

          /* Correct from the box actually drawn -- but only when that box is
             believable.

             A cover finishing its load disposes and rebuilds the geometry, and
             for the frame in between Box3 has no bounding box to read and
             returns a degenerate one: widths of twenty thousand pixels have
             been measured here. Fed into this correction it threw the book
             hundreds of pixels sideways for a frame, which is the snap you get
             when opening a book whose art has not finished decoding. */
          mesh.updateWorldMatrix(true, true);
          const drawn = screenRect(mesh);
          const sane =
            Number.isFinite(drawn.left) &&
            Number.isFinite(drawn.width) &&
            drawn.width > cw * 0.05 &&
            drawn.width < cw * 1.5;
          if (sane) {
            const err = drawn.left + drawn.width / 2 - heroPageX;
            mesh.userData.xCorr = corr - (err * UNIT) / fit;
          }
        });
        group.position.y = ch * TOP_ANCHOR * UNIT;
        return;
      }

      /* Where the picked book sits on the belt, so the rest of the run knows
         which way to get out of its way. */
      const focusMesh = focus.slug
        ? meshes.find(m => m.userData.slug === focus.slug)
        : null;
      const focusP = focusMesh ? focusMesh.userData.rank - offset : 0;
      /* Far enough to clear the viewport, since nothing shrinks any more. */
      const clear = (ch * 1.05 * UNIT) / fit;

      meshes.forEach(mesh => {
        const { rank, out, gone } = mesh.userData;
        if (gone && out > 0.98) {
          mesh.visible = false;
          return;
        }
        const p = rank - offset;
        const leaveX = (out * LEAVE_X * UNIT) / fit;
        const { y, z } = placeAt(p, w, depth);
        /* Screen y grows downward, world y grows up. */
        const restY = -y * UNIT;
        /* Positive tilts the top board toward the camera. Negative points it
           away and you end up looking at the underside, which is the same
           inverted-lean mistake the CSS version made. */
        const restRotX = (phiAt(p) * Math.PI) / 180;

        const t = mesh.userData.slug === focus.slug ? focus.t : 0;
        if (t === 0) {
          /* The rest of the run splits and slides out of the way -- the books
             above go up, the books below go down, and the pile opens where the
             picked one was.

             It used to shrink them in place instead. That reads as the shelf
             being deleted rather than making room, and it is not what the
             reference does: there the whole thing is one long scroll, so the
             neighbours simply travel off the top and bottom of the screen.
             This is the same gesture without the scroll.

             A uniform push rather than one that grows with distance, so the
             run keeps its own spacing on the way out and moves as two blocks
             rather than fanning apart. */
          /* World y is up but restY is negated from a screen-down y, so the
             book ABOVE the pick (smaller p) needs a positive push. Getting
             this backwards drives the lower half of the run up into frame,
             which is precisely the failure the old shrink was avoiding. */
          const away = p < focusP ? 1 : -1;
          const push = away * clear * easeInOut(focus.t);
          mesh.position.set(leaveX, restY + push, z * UNIT);
          mesh.rotation.set(restRotX, 0, 0);
          mesh.scale.setScalar(1);
          /* Left to the frustum: once pushed they are off screen, and three
             culls them for free. */
          mesh.visible = true;
          for (const part of mesh.userData.parts) {
            part.renderOrder = Math.round(p * 10);
          }
          return;
        }
        mesh.visible = true;

        const e = easeInOut(t);
        mesh.position.set(
          heroX * e,
          restY + (flightY - restY) * e,
          z * UNIT * (1 - e)
        );
        /* Up onto its end and round to face the camera. A shade of yaw at the
           end keeps it a three-quarter view rather than a flat elevation. */
        restEuler.set(restRotX, 0, 0);
        restQuat.setFromEuler(restEuler);
        mesh.quaternion.slerpQuaternions(restQuat, heroQuat, e);

        /* Lean toward the pointer, faded in with the flight rather than
           switched on at the end of it.

           This used to be gated on e > 0.99. With the cursor anywhere off
           centre the lean appeared in a single frame as the book landed, and
           vanished in a single frame as it left -- which is the snap either
           side of the animation. Scaling by e instead means the lean is
           nothing while the book is in the pile, full once it has arrived, and
           continuous through the handover to the reader, which applies the
           same rotation at full strength. */
        const track = pointerRef.current;
        if (track.x || track.y) {
          trackEuler.set(
            track.y * TRACK_PITCH * e,
            track.x * TRACK_YAW * e,
            0
          );
          trackQuat.setFromEuler(trackEuler);
          mesh.quaternion.premultiply(trackQuat);
        }
        mesh.scale.setScalar(1 + (heroScale - 1) * e);
        for (const part of mesh.userData.parts) part.renderOrder = 999;
      });
      /* The sentence above the pile rides the belt: however far the first
         slot has travelled up, it goes the same distance, so it scrolls away
         with the books instead of hanging over them. */
      const head = headRef?.current;
      if (head) {
        head.style.setProperty('--shelfShift', `${placeAt(-offset, w, depth).y * fit}px`);
      }
      /* Sit the run where the CSS one sits: top of the curve near the top of
         the screen rather than centred on it. */
      group.position.y = ch * TOP_ANCHOR * UNIT;
    };

    let frame = 0;
    /* Declarations, not expressions: they hoist, so the texture-load callbacks
       further up can ask for a frame. */
    function render() {
      frame = 0;
      const focus = focusRef.current;
      /* Advance the travel, then keep asking for frames until it settles. */
      if (focus.since) {
        const raw = Math.min(1, (performance.now() - focus.since) / FOCUS_MS);
        focus.t = focus.from + (focus.target - focus.from) * raw;
        if (raw >= 1) focus.since = 0;
      }
      /* Ease toward the pointer, and keep asking for frames until it settles
         so the book glides to a stop instead of stepping. */
      const p = pointerRef.current;
      p.x += (p.tx - p.x) * TRACK_EASE;
      p.y += (p.ty - p.y) * TRACK_EASE;
      const settling =
        Math.abs(p.tx - p.x) > 0.001 || Math.abs(p.ty - p.y) > 0.001;

      let moving = false;
      for (const mesh of meshes) {
        const d = mesh.userData;
        const rankLeft = d.target - d.rank;
        const outLeft = d.gone - d.out;
        if (Math.abs(rankLeft) > 0.002 || Math.abs(outLeft) > 0.002) {
          d.rank += rankLeft * RERANK_EASE;
          d.out += outLeft * LEAVE_EASE;
          moving = true;
        } else {
          d.rank = d.target;
          d.out = d.gone;
        }
      }

      layout();
      renderer.render(scene, camera);
      if (focus.since || moving || (settling && focus.t > 0.99)) schedule();
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(render);
    }

    /* Trails the cursor on its own frames. Written to a ref and eased in the
       render loop rather than applied on the event, so a fast mouse cannot
       outrun the frame rate or force a render per move. */
    const onPointerMove = e => {
      const rect = canvas.getBoundingClientRect();
      const p = pointerRef.current;
      p.tx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      p.ty = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      p.active = true;
      schedule();
    };

    const onPointerLeave = () => {
      const p = pointerRef.current;
      p.tx = 0;
      p.ty = 0;
      schedule();
    };

    const onWheel = e => {
      /* The canvas is fixed and covers the whole viewport in the reader, so
         swallowing the wheel here would stop the document scrolling at all --
         which is the reader's only navigation. The pile owns the wheel; the
         reader does not. */
      if (readerRef?.current?.active) return;
      e.preventDefault();
      const max = Math.max(0, shelfSize - 1);
      const next = offsetRef.current + e.deltaY / WHEEL_PER_BOOK;
      offsetRef.current = next < 0 ? 0 : next > max ? max : next;
      schedule();
    };

    const onClick = e => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(
        meshes.filter(m => !m.userData.gone),
        true
      )[0];
      if (!hit) return;
      const mesh = hit.object;
      onPickRef.current?.(mesh.userData.slug, {
        rect: screenRect(mesh),
        /* The angle it is sitting at, so the flight leaves from that pose. */
        phi: (mesh.rotation.x * 180) / Math.PI,
      });
    };

    const onResize = () => {
      resize();
      schedule();
    };

    /* The reader places every book from the scroll position, so a scroll has
       to ask for a frame. Without this the scene only redrew when something
       else did, such as the pointer moving: scroll with a still cursor and the
       book froze, then jumped to catch up on the next frame anything drew. */
    const onScroll = () => {
      if (readerRef?.current?.active) schedule();
    };

    resize();
    render();

    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('click', onClick);
    /* Only where there is a real pointer: a tap would jam the book off-axis. */
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (fine) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      document.addEventListener('pointerleave', onPointerLeave);
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });

    /* And correct it the moment the element actually has a size, rather than
       trusting the one measurement taken at mount. */
    const observer = new ResizeObserver(onResize);
    observer.observe(canvas);

    stateRef.current = {
      schedule,
      /* The books to show, in the order to show them. A book staying on moves
         to its new slot; one leaving slides out; one coming back that had
         fully left starts at its new slot, so it slides in rather than
         travelling there through the pile. */
      setShelf(slugs) {
        const at = new Map(slugs.map((slug, i) => [slug, i]));
        const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        for (const mesh of meshes) {
          const d = mesh.userData;
          const slot = at.get(d.slug);
          if (slot === undefined) {
            d.gone = 1;
          } else {
            if (d.out > 0.98) d.rank = slot;
            d.target = slot;
            d.gone = 0;
          }
          if (still) {
            d.rank = d.target;
            d.out = d.gone;
          }
        }
        shelfSize = slugs.length;
        const max = Math.max(0, shelfSize - 1);
        if (offsetRef.current > max) offsetRef.current = max;
        schedule();
      },
      focus(slug, { instant = false } = {}) {
        const f = focusRef.current;
        if (slug) f.slug = slug;
        /* Landing directly on a book's URL has nothing to fly from, so the
           book is simply already there. Animating it would show the pile for
           760ms on a page that never had a pile. */
        if (instant) {
          f.t = slug ? 1 : 0;
          f.target = f.t;
          f.since = 0;
          schedule();
          return;
        }
        f.from = f.t;
        f.target = slug ? 1 : 0;
        f.since = performance.now();
        schedule();
      },
    };

    return () => {
      live = false;
      if (frame) cancelAnimationFrame(frame);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('click', onClick);
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      observer.disconnect();
      stateRef.current = null;
      cloth.dispose();
      glitter.dispose();
      pages.dispose();
      /* cleanScene walks materials and disposes every texture on them, which
         covers the per-face clones and the spine labels. */
      cleanScene(scene);
      cleanRenderer(renderer);
    };
    /* Refs only -- see onPickRef above. */
  }, [offsetRef, readerRef, headRef]);

  /* Keyed on the order itself, so a new array with the same books does not
     restart anything. */
  const shelfKey = shelf ? shelf.join('|') : '';
  useEffect(() => {
    if (shelf) stateRef.current?.setShelf(shelf);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shelfKey stands in for shelf
  }, [shelfKey]);

  /* Focus is a prop so the page owns which book is open, but the travel is run
     inside the scene -- the mesh moves, nothing is cloned into the DOM. */
  const mountedRef = useRef(false);
  useEffect(() => {
    const instant = !mountedRef.current;
    mountedRef.current = true;
    stateRef.current?.focus(focusedSlug || null, { instant });
  }, [focusedSlug]);

  return (
    <canvas
      ref={canvasRef}
      className={`${styles.canvas} ${className || ''}`}
      aria-label="Bookshelf"
    />
  );
}
